
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { fetchAssociationId } from "@/utils/associationUtils";

interface Section {
  id: string;
  name: string;
  position: number;
  section_type: 'DOCUMENTS' | 'FAQ';
}

interface Document {
  id: string;
  name: string;
  display_name?: string;
  file_path: string;
  section_id: string;
  created_at: string;
  size: number;
  position: number;
  content_type: string;
  created_by: string | null;
  creator_email: string | null;
  creator_name: string | null;
  organization_id: string;
}

export const useDocumentsManager = () => {
  const [sections, setSections] = useState<Section[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedSection, setSelectedSection] = useState<Section | null>(null);
  const [isLoadingSections, setIsLoadingSections] = useState(false);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrgData = async () => {
      try {
        // First, get the member's organization
        const memberId = sessionStorage.getItem('organization_member_id');
        
        if (!memberId) {
          console.error('No organization member ID found in session');
          toast.error('Could not determine your organization membership');
          return;
        }
        
        const { data: memberData, error: memberError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('id', memberId)
          .single();
        
        if (memberError || !memberData) {
          console.error('Error fetching organization member data:', memberError);
          toast.error('Could not determine your organization');
          return;
        }
        
        // Set the organization UUID (not the numeric association_id)
        const orgUuid = memberData.organization_id;
        setOrganizationId(orgUuid);
        
        // Now load the sections using this UUID
        if (orgUuid) {
          await ensureMandatorySections(orgUuid);
          loadSections(orgUuid);
        }
      } catch (error) {
        console.error('Error in fetchOrgData:', error);
        toast.error('Failed to fetch organization data');
      }
    };

    fetchOrgData();
    
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const ensureMandatorySections = async (orgId: string) => {
    try {
      // Check if at least one FAQ section exists
      const { data: faqSection } = await supabase
        .from('document_sections')
        .select('id')
        .eq('organization_id', orgId)
        .eq('section_type', 'FAQ')
        .limit(1);

      // If no FAQ section exists, create a default one
      if (!faqSection || faqSection.length === 0) {
        // Get the highest position number
        const { data: maxPosition } = await supabase
          .from('document_sections')
          .select('position')
          .eq('organization_id', orgId)
          .order('position', { ascending: false })
          .limit(1);

        const nextPosition = maxPosition && maxPosition.length > 0 ? maxPosition[0].position + 1 : 3;

        const { error: insertError } = await supabase
          .from('document_sections')
          .insert({
            name: 'FAQ',
            organization_id: orgId,
            position: nextPosition,
            section_type: 'FAQ',
            created_by: (await supabase.auth.getUser()).data.user?.id
          });

        if (insertError) {
          console.error('Error creating FAQ section:', insertError);
        }
      }
    } catch (error) {
      console.error('Error ensuring mandatory sections:', error);
    }
  };

  const loadSections = async (orgId: string) => {
    setIsLoadingSections(true);
    try {
      console.log('Fetching sections for organization:', orgId);
      const { data, error } = await supabase
        .from('document_sections')
        .select('*')
        .eq('organization_id', orgId)
        .order('position');
      
      if (error) throw error;
      console.log('Loaded sections:', data);
      setSections(data || []);
    } catch (error) {
      console.error('Error loading sections:', error);
      toast.error('Failed to load document sections');
    } finally {
      setIsLoadingSections(false);
    }
  };

  const loadDocuments = async (sectionId: string) => {
    if (!organizationId) return;
    
    setIsLoadingDocuments(true);
    try {
      console.log(`Loading documents for section ${sectionId} in organization ${organizationId}`);
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('section_id', sectionId)
        .eq('organization_id', organizationId)
        .order('position');
      
      if (error) throw error;
      console.log('Loaded documents:', data);
      setDocuments(data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const handleSectionSelect = (section: Section) => {
    setSelectedSection(section);
    if (section.section_type !== 'FAQ') {
      loadDocuments(section.id);
    }
  };

  const handleDocumentDeleted = (documentId: string) => {
    setDocuments(documents.filter(d => d.id !== documentId));
  };

  const handleDocumentUpdated = (docId: string, newDisplayName: string) => {
    setDocuments(prevDocuments => 
      prevDocuments.map(doc => 
        doc.id === docId 
          ? { ...doc, display_name: newDisplayName }
          : doc
      )
    );
  };

  const handleDocumentUploaded = () => {
    if (selectedSection && selectedSection.section_type !== 'FAQ') {
      loadDocuments(selectedSection.id);
    }
  };

  const handleSectionDeleted = () => {
    if (organizationId) {
      loadSections(organizationId);
    }
    setSelectedSection(null);
    setDocuments([]);
  };

  const handleDownload = async (doc: Document) => {
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(doc.file_path, 60);
      
      if (error) throw error;
      
      if (data?.signedUrl) {
        const link = document.createElement('a');
        link.href = data.signedUrl;
        link.download = doc.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  return {
    sections,
    documents,
    selectedSection,
    isLoadingSections,
    isLoadingDocuments,
    isMobileView,
    organizationId,
    setSections,
    setSelectedSection,
    handleSectionSelect,
    handleDocumentDeleted,
    handleDocumentUploaded,
    handleDocumentUpdated,
    handleSectionDeleted,
    handleDownload
  };
};
